import { FC, useEffect, useState } from 'react'
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import { privacidadeService } from '@/services/privacidadeService'
import {
  Expurgo,
  FUNDAMENTO_EXPURGO_LABEL,
  FiltroPrevia,
  FundamentoExpurgo,
  PreviaExpurgo,
} from '@/types/privacidade'
import { BloqueiosPrevia, ItensExpurgoTabela } from './ItensExpurgoTabela'
import { mensagemErroPrivacidade } from './comum'

const FUNDAMENTOS = Object.keys(FUNDAMENTO_EXPURGO_LABEL) as FundamentoExpurgo[]

interface Props {
  aberto: boolean
  /** Pré-preenche o fundamento "Solicitação do titular" com este protocolo. */
  protocoloInicial?: string
  onFechar: () => void
  onCriado: (e: Expurgo) => void
}

const separarIds = (texto: string): string[] =>
  texto
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean)

/**
 * Novo expurgo (AN-12 §5.1): documentos (já removidos logicamente) ou uma reserva inteira →
 * prévia calculada no servidor (cadeia de versões + cópias por reuso, bloqueios) → pedido.
 * Com bloqueio não há pedido. O servidor recalcula tudo de novo ao gravar.
 */
export const NovoExpurgoDialog: FC<Props> = ({ aberto, protocoloInicial, onFechar, onCriado }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [modo, setModo] = useState<'documentos' | 'reserva'>('documentos')
  const [idsTexto, setIdsTexto] = useState('')
  const [reservaId, setReservaId] = useState('')
  const [fundamentoTipo, setFundamentoTipo] = useState<FundamentoExpurgo>('SOLICITACAO_TITULAR')
  const [fundamentoTexto, setFundamentoTexto] = useState('')
  const [protocolo, setProtocolo] = useState('')
  const [previa, setPrevia] = useState<PreviaExpurgo | null>(null)
  const [calculando, setCalculando] = useState(false)
  const [erroPrevia, setErroPrevia] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)

  useEffect(() => {
    if (!aberto) return
    setModo('documentos')
    setIdsTexto('')
    setReservaId('')
    setFundamentoTipo(protocoloInicial ? 'SOLICITACAO_TITULAR' : 'OUTRO')
    setFundamentoTexto('')
    setProtocolo(protocoloInicial ?? '')
    setPrevia(null)
    setErroPrevia(null)
  }, [aberto, protocoloInicial])

  const filtro = (): FiltroPrevia =>
    modo === 'documentos' ? { documentoIds: separarIds(idsTexto) } : { reservaId: reservaId.trim() }

  const alvoPreenchido = modo === 'documentos' ? separarIds(idsTexto).length > 0 : reservaId.trim().length > 0

  // Mudou o alvo: a prévia anterior não vale mais.
  useEffect(() => {
    setPrevia(null)
  }, [modo, idsTexto, reservaId])

  const calcular = async () => {
    setCalculando(true)
    setErroPrevia(null)
    try {
      setPrevia(await privacidadeService.previa(filtro()))
    } catch (e) {
      setErroPrevia(mensagemErroPrivacidade(e, 'Falha ao calcular a prévia'))
    } finally {
      setCalculando(false)
    }
  }

  const precisaProtocolo = fundamentoTipo === 'SOLICITACAO_TITULAR'
  const podePedir =
    !!previa &&
    previa.expurgavel !== false &&
    previa.bloqueios.length === 0 &&
    previa.itens.length > 0 &&
    fundamentoTexto.trim().length > 0 &&
    (!precisaProtocolo || protocolo.trim().length > 0)

  const pedir = async () => {
    setEnviando(true)
    try {
      const criado = await privacidadeService.criarExpurgo({
        ...filtro(),
        fundamentoTipo,
        fundamentoTexto: fundamentoTexto.trim(),
        solicitacaoProtocolo: protocolo.trim() || undefined,
        // Presa à prévia vista: se o conjunto mudou, 409 PREVIA_DESATUALIZADA.
        previaHash: previa?.previaHash,
      })
      enqueueSnackbar(`Expurgo ${criado.codigo} registrado — aguardando a 1ª aprovação (DPO)`, { variant: 'success' })
      onCriado(criado)
    } catch (e) {
      enqueueSnackbar(mensagemErroPrivacidade(e, 'Falha ao registrar o expurgo'), { variant: 'error' })
      // Bloqueio novo ou conjunto mudou: a prévia mostrada não vale mais.
      setPrevia(null)
    } finally {
      setEnviando(false)
    }
  }

  return (
    <Dialog open={aberto} onClose={enviando ? undefined : onFechar} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Novo expurgo</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info">
            Só documentos já removidos (exclusão lógica) entram na fila. Versões anteriores e cópias por reuso
            entram junto, calculadas pelo servidor.
          </Alert>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={modo}
            onChange={(_, v) => v && setModo(v)}
            aria-label="Alvo do expurgo"
          >
            <ToggleButton value="documentos">Documentos</ToggleButton>
            <ToggleButton value="reserva">Reserva inteira</ToggleButton>
          </ToggleButtonGroup>
          {modo === 'documentos' ? (
            <TextField
              label="Ids dos documentos"
              helperText="Um por linha, ou separados por vírgula."
              multiline
              rows={3}
              value={idsTexto}
              onChange={(e) => setIdsTexto(e.target.value)}
            />
          ) : (
            <TextField
              label="Id da reserva"
              helperText="Todos os documentos removidos da reserva."
              value={reservaId}
              onChange={(e) => setReservaId(e.target.value)}
            />
          )}
          <Stack direction="row" spacing={1} alignItems="center">
            <Button variant="outlined" onClick={calcular} disabled={!alvoPreenchido || calculando}>
              Calcular prévia
            </Button>
            {calculando && <CircularProgress size={18} aria-label="Calculando prévia" />}
          </Stack>
          {erroPrevia && <Alert severity="error">{erroPrevia}</Alert>}
          {previa && (
            <Stack spacing={1} data-testid="previa-expurgo">
              <BloqueiosPrevia bloqueios={previa.bloqueios} />
              {previa.itens.length === 0 ? (
                <Alert severity="warning">Nenhum documento removido encontrado para este alvo.</Alert>
              ) : (
                <>
                  <Typography variant="subtitle2">{previa.itens.length} arquivo(s) na prévia</Typography>
                  <ItensExpurgoTabela itens={previa.itens} />
                </>
              )}
            </Stack>
          )}
          <TextField
            select
            label="Fundamento"
            value={fundamentoTipo}
            onChange={(e) => setFundamentoTipo(e.target.value as FundamentoExpurgo)}
          >
            {FUNDAMENTOS.map((f) => (
              <MenuItem key={f} value={f}>
                {FUNDAMENTO_EXPURGO_LABEL[f]}
              </MenuItem>
            ))}
          </TextField>
          {precisaProtocolo && (
            <TextField
              label="Protocolo da solicitação do titular"
              required
              value={protocolo}
              onChange={(e) => setProtocolo(e.target.value)}
              placeholder="TIT-2026-00012"
            />
          )}
          <TextField
            label="Registro do fundamento"
            required
            multiline
            rows={3}
            value={fundamentoTexto}
            onChange={(e) => setFundamentoTexto(e.target.value)}
            inputProps={{ maxLength: 1000 }}
            helperText="Fica na trilha imutável do expurgo. Não escreva dados pessoais."
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onFechar} disabled={enviando}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={pedir} disabled={!podePedir || enviando}>
          {enviando ? 'Registrando…' : 'Pedir expurgo'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default NovoExpurgoDialog
